/* eslint-disable camelcase */
import { useMemo } from 'react';

import { TOTAL_EQUITY_DECIMAL, VANILLA_SETTLING_ASSET } from 'constants/constants';
import { LOCALES } from 'constants/dateTime';
import { CURRENCY_TYPE } from 'constants/enums';
import { formatFiat } from 'helpers/currency';
import {
  addZeroesUntilCorrectPrecision,
  convertExponentialToDecimal,
  convertFromUSDtoINR,
  cropAfterDecimals,
  getBalanceBySymbol,
  isEmpty,
} from 'helpers/utils';
import { inrConversionRateSelector } from 'selectors/otherSelectors';
import {
  tradingCreditsSelector,
  withdrawalLimitsSelector,
} from 'selectors/tradeSelectors';
import {
  balanceBySymbolSelector,
  deltaCashSelectorUsd,
  enableWalletsSelector,
} from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';

// Use this hook for wallet related data
// if any other formatting needed add a new key in which section needed and format as needed
// don't remove the exisiting keys as of now used in desktop - wallet menu & withdrawal page , mobile - balance tab & withdrawal page

// @dileep we need move this to selectors

const useWalletIndianExchange = () => {
  const balances = useAppSelector(enableWalletsSelector);
  const inrConversionRate = useAppSelector(inrConversionRateSelector);
  const tradingCredits = useAppSelector(tradingCreditsSelector);
  const allBalances = useAppSelector(balanceBySymbolSelector);
  const deltaCashAmount = useAppSelector(deltaCashSelectorUsd);
  const withdrawalLimits = useAppSelector(withdrawalLimitsSelector);
  const usdBalanceObject = useMemo(() => balances?.USD ?? {}, [balances]);

  const usdAssetMinPrecision = usdBalanceObject?.asset?.minimum_precision ?? 2;

  const { balance, balance_inr: balanceInr } = getBalanceBySymbol(
    VANILLA_SETTLING_ASSET,
    allBalances
  ) ?? {
    balance: 0,
    balance_inr: 0,
  };

  // Account Value
  const accountValue = useMemo(() => {
    const accountValueAmount = Number(usdBalanceObject?.balance ?? 0);

    return {
      accountValueAmount,
    };
  }, [usdBalanceObject]);

  // Delta Cash
  const deltaCash = useMemo(() => {
    const deltaCashFormattedInUSD = formatFiat(
      CURRENCY_TYPE.USD,
      Number(deltaCashAmount ?? 0),
      LOCALES.INDIA
    );

    const deltaCashFormattedInINR = formatFiat(
      CURRENCY_TYPE.INR,
      convertFromUSDtoINR(Number(deltaCashAmount ?? 0), inrConversionRate),
      LOCALES.INDIA
    );

    return {
      deltaCashFormattedInUSD,
      deltaCashFormattedInINR,
    };
  }, [deltaCashAmount, inrConversionRate]);

  // Trading Credits
  const tradingCredit = useMemo(() => {
    if (isEmpty(tradingCredits)) {
      return {
        tradingCreditAmount: 0,
        tradingCreditFormattedInUSD: formatFiat(CURRENCY_TYPE.USD, 0),
        tradingCreditFormattedInINR: formatFiat(CURRENCY_TYPE.INR, 0, LOCALES.INDIA),
      };
    }

    let tradingCreditAmount = 0;

    Object.values(tradingCredits).forEach(credit => {
      // eslint-disable-next-line func-names
      tradingCreditAmount += credit.amount;
    });

    const tradingCreditFormattedInUSD = formatFiat(
      CURRENCY_TYPE.USD,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(tradingCreditAmount, usdAssetMinPrecision),
        usdAssetMinPrecision
      )
    );

    const tradingCreditFormattedInINR = formatFiat(
      CURRENCY_TYPE.INR,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(
          convertFromUSDtoINR(tradingCreditAmount, inrConversionRate),
          usdAssetMinPrecision
        ),
        usdAssetMinPrecision
      ),
      LOCALES.INDIA
    );

    return {
      tradingCreditAmount,
      tradingCreditFormattedInUSD,
      tradingCreditFormattedInINR,
    };
  }, [tradingCredits, usdAssetMinPrecision, inrConversionRate]);

  // Account Balance
  const accountBalance = useMemo(() => {
    const accountBalanceFormattedInUSD = formatFiat(
      CURRENCY_TYPE.USD,
      convertExponentialToDecimal(
        cropAfterDecimals(
          addZeroesUntilCorrectPrecision(Number(balance), usdAssetMinPrecision),
          usdAssetMinPrecision
        )
      ),
      LOCALES.GLOBAL,
      {
        maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
      }
    );

    const accountBalanceFormattedInINR = formatFiat(
      CURRENCY_TYPE.INR,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(Number(balanceInr), usdAssetMinPrecision),
        usdAssetMinPrecision
      ),
      LOCALES.INDIA,
      {
        maximumFractionDigits: TOTAL_EQUITY_DECIMAL,
      }
    );

    return { accountBalanceFormattedInUSD, accountBalanceFormattedInINR };
  }, [balance, usdAssetMinPrecision, balanceInr]);

  // WITHDRAWABLE BALANCE NEED TO COME VIA WITHDRAWAL LIMIT - [DEIFE-355]
  const withdrawableLimitsBalance = useMemo(() => {
    const withdrawableLimitsBalanceAmountInINR = cropAfterDecimals(
      addZeroesUntilCorrectPrecision(
        convertFromUSDtoINR(
          Number(withdrawalLimits?.max_withdrawal_after_forfeiting),
          inrConversionRate
        ),
        usdAssetMinPrecision
      ),
      usdAssetMinPrecision
    );

    const withdrawableLimitsBalanceFormattedInUSD = formatFiat(
      CURRENCY_TYPE.USD,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(
          Number(withdrawalLimits?.max_withdrawal_after_forfeiting),
          usdAssetMinPrecision
        ),
        usdAssetMinPrecision
      )
    );

    const withdrawableLimitsBalanceAmountInINRFormattedInINR = formatFiat(
      CURRENCY_TYPE.INR,
      cropAfterDecimals(
        addZeroesUntilCorrectPrecision(
          Number(withdrawableLimitsBalanceAmountInINR),
          usdAssetMinPrecision
        ),
        usdAssetMinPrecision
      ),
      LOCALES.INDIA
    );

    return {
      withdrawableLimitsBalanceAmountInINR,
      withdrawableLimitsBalanceFormattedInUSD,
      withdrawableLimitsBalanceAmountInINRFormattedInINR,
    };
  }, [
    inrConversionRate,
    withdrawalLimits?.max_withdrawal_after_forfeiting,
    usdAssetMinPrecision,
  ]);

  return {
    accountValue,
    deltaCash,
    tradingCredit,
    accountBalance,
    inrConversionRate,
    withdrawableLimitsBalance,
  };
};

export default useWalletIndianExchange;
